import { DataType } from '@mode-switch/express';

import { ReportTypes } from '@mode/shared/contract-common';

// Line between types and util is blurry

import { CompiledAttr, CompiledFormula, Switch } from '@mode/shared/util-switch';
import { ReportFilterControlFactory } from './report-filter-control';

export enum ReportFilterFieldType {
  Field = 'FIELD',
  Folder = 'FOLDER',
}

export interface ReportFilterField {
  readonly id: string;
  readonly type: ReportFilterFieldType.Field;
  readonly name: string;
  readonly control: ReportTypes.FilterDefinition;
  readonly inUse: boolean;
  readonly isAggregate: boolean;
  readonly isCalc: boolean;
  readonly formulaType: DataType.Name;
}

export interface ReportFilterFieldFolder {
  readonly id: string;
  readonly type: ReportFilterFieldType.Folder;
  readonly name: string;
  readonly fields: ReportFilterField[];
  readonly isCalc: boolean;
  readonly formulaType: DataType.Name;
}

export type ReportFilterFieldTypes = ReportFilterField | ReportFilterFieldFolder;

function buildFilterField(
  id: string,
  name: string,
  ctrl: ReportTypes.FilterDefinition,
  filterControls: ReadonlyArray<ReportTypes.FilterDefinition>,
  formula: CompiledFormula,
  isAggregate?: boolean
): ReportFilterField {
  const isCalc = (formula as CompiledAttr).isCalc;
  const existing = filterControls.find(({ name, dataType }) => name === ctrl.name && dataType === ctrl.dataType);
  if (existing) {
    name += ' (Added)';
  }

  if (isAggregate) {
    name += ' (Unsupported aggregate)';
  }
  return {
    type: ReportFilterFieldType.Field,
    id,
    name,
    inUse: Boolean(existing),
    isAggregate,
    control: existing || ctrl,
    isCalc,
    formulaType: formula.attrDataType,
  };
}

export class ReportFilterFieldFactory {
  static buildDefaultSelectFilterField(
    formula: CompiledFormula,
    filterControls: ReadonlyArray<ReportTypes.FilterDefinition>
  ): ReportFilterField {
    const ctrl = ReportFilterControlFactory.buildDefaultSelectControl(formula);
    return buildFilterField(
      `${formula.fieldName}:${Switch.Compat.makeDataType(formula.dataType)}`,
      ctrl.name,
      ctrl,
      filterControls,
      formula
    );
  }

  static buildDefaultRangeFilterField(
    formula: CompiledFormula,
    filterControls: ReadonlyArray<ReportTypes.FilterDefinition>,
    isAggregate?: boolean
  ): ReportFilterField {
    const ctrl = ReportFilterControlFactory.buildDefaultRangeControl(formula);
    return buildFilterField(
      `${formula.fieldName}:${Switch.Compat.makeDataType(formula.dataType)}`,
      ctrl.name,
      ctrl,
      filterControls,
      formula,
      isAggregate
    );
  }

  static buildDefaultDateFilterFolder(
    formula: CompiledFormula,
    filterControls: ReadonlyArray<ReportTypes.FilterDefinition>
  ): ReportFilterFieldFolder {
    return {
      type: ReportFilterFieldType.Folder,
      name: formula.fieldName,
      id: `${formula.fieldName}:CONTAINING_FOLDER`,
      fields: this.buildDefaultDateFilterFields(formula, filterControls),
      isCalc: false,
      formulaType: formula.attrDataType,
    };
  }

  static buildDefaultDateFilterFields(
    formula: CompiledFormula,
    filterControls: ReadonlyArray<ReportTypes.FilterDefinition>
  ): ReportFilterField[] {
    const filterFields = new Array<ReportFilterField>();

    if (formula.dataType !== DataType.Name.DateTime && formula.dataType !== DataType.Name.Timestamp) {
      return filterFields;
    }

    function addDateSubfield(
      id: string,
      name: string,
      buildControl: (formula: CompiledFormula) => ReportTypes.FilterDefinition
    ) {
      filterFields.push(buildFilterField(id, name, buildControl(formula), filterControls, formula));
    }

    const vegasDataType = Switch.Compat.makeDataType(formula.dataType);

    // Exact (CONTINUOUS)
    addDateSubfield(
      `${formula.fieldName}:${vegasDataType}`,
      formula.fieldName,
      ReportFilterControlFactory.buildDefaultDateControl
    );

    // Year
    addDateSubfield(
      `Year(${formula.fieldName}):${vegasDataType}`,
      'year',
      ReportFilterControlFactory.buildDefaultYearControl
    );

    // Quarter
    addDateSubfield(
      `Quarter(${formula.fieldName}):${vegasDataType}`,
      'quarter',
      ReportFilterControlFactory.buildDefaultQuarterControl
    );

    // Month
    addDateSubfield(
      `Month(${formula.fieldName}):${vegasDataType}`,
      'month',
      ReportFilterControlFactory.buildDefaultMonthControl
    );

    // Day of Month
    addDateSubfield(
      `Day(${formula.fieldName}):${vegasDataType}`,
      'day of month',
      ReportFilterControlFactory.buildDefaultDayControl
    );

    // Week Number
    addDateSubfield(
      `Week(${formula.fieldName}):${vegasDataType}`,
      'week number',
      ReportFilterControlFactory.buildDefaultWeekControl
    );

    // Day of Week
    addDateSubfield(
      `Weekday(${formula.fieldName}):${vegasDataType}`,
      'day of week',
      ReportFilterControlFactory.buildDefaultWeekdayControl
    );

    // Hour
    addDateSubfield(
      `Hour(${formula.fieldName}):${vegasDataType}`,
      'hour',
      ReportFilterControlFactory.buildDefaultHourControl
    );

    // Minute
    addDateSubfield(
      `Minute(${formula.fieldName}):${vegasDataType}`,
      'minute',
      ReportFilterControlFactory.buildDefaultMinuteControl
    );

    // Second
    addDateSubfield(
      `Second(${formula.fieldName}):${vegasDataType}`,
      'second',
      ReportFilterControlFactory.buildDefaultSecondControl
    );

    return filterFields;
  }
}
